/**
 * Other-school staff linking for SEIS / staff import.
 *
 * School-scoped user lists never see people whose schoolIds omit this campus.
 * These helpers classify a district-wide email hit and attach it to the SEIS
 * unmatched list so the importer can add THIS school (append only) and link.
 *
 * Keep addSchoolDecision() in sync with functions/modules/userSchoolLink.js.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase.js'
import { ROLE_DISPLAY_NAMES } from '@/config/roles'

/** Roles that reach every school by role — do not stamp schoolIds (would narrow admin). */
export const DISTRICT_ONLY_ROLES = ['admin', 'district_admin', 'district_coordinator']

/** allSchools flag is honored for these CM roles only (mirrors schoolScope.js). */
export const ALL_SCHOOLS_CM_ROLES = ['case_manager', 'case_manager_full', 'speech_case_manager']

const EMAIL_CAP = 80

export function normalizeStaffEmail(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
}

/**
 * Whether this existing profile may receive the import school on schoolIds.
 * @returns {{ action: 'stamp'|'link_only'|'already'|'refuse', reason: string|null }}
 */
export function addSchoolDecision(user = {}, schoolId = '') {
  if (!schoolId) return { action: 'refuse', reason: 'no_school' }
  if (user.status === 'pending' || user.status === 'suspended') {
    return { action: 'refuse', reason: user.status }
  }
  const ids = Array.isArray(user.schoolIds) ? user.schoolIds.filter(Boolean) : []
  if (ids.includes(schoolId)) return { action: 'already', reason: 'already_member' }
  if (DISTRICT_ONLY_ROLES.includes(user.role)) {
    return { action: 'link_only', reason: 'district_role' }
  }
  if (user.allSchools === true && ALL_SCHOOLS_CM_ROLES.includes(user.role)) {
    return { action: 'link_only', reason: 'all_schools' }
  }
  return { action: 'stamp', reason: null }
}

export function collectUnmatchedStaffEmails(analysis) {
  const emails = []
  for (const cm of analysis?.caseManagers?.unmatched || []) {
    const email = normalizeStaffEmail(cm.email)
    if (email) emails.push(email)
  }
  return [...new Set(emails)]
}

export function toOtherSchoolUser(match = {}, schoolId = '') {
  const decision = addSchoolDecision(match, schoolId)
  const names = Array.isArray(match.schoolNames) ? match.schoolNames.filter(Boolean) : []
  return {
    uid: match.uid || '',
    userName: match.name || match.email || match.uid || '',
    email: normalizeStaffEmail(match.email),
    role: match.role || '',
    status: match.status || '',
    schoolIds: Array.isArray(match.schoolIds) ? match.schoolIds : [],
    schoolNames: names,
    allSchools: match.allSchools === true,
    alreadyAtSchool: match.alreadyAtSchool === true,
    addAction: decision.action,
    addReason: decision.reason
  }
}

/** Attach district email hits onto unmatched case managers (mutates analysis). */
export function attachOtherSchoolMatches(analysis, matches = [], schoolId = '') {
  const byEmail = new Map()
  for (const match of matches) {
    const email = normalizeStaffEmail(match?.email)
    if (email) byEmail.set(email, match)
  }
  for (const cm of analysis?.caseManagers?.unmatched || []) {
    const email = normalizeStaffEmail(cm.email)
    const match = email ? byEmail.get(email) : null
    if (!match?.uid) continue
    if (match.alreadyAtSchool) continue
    cm.otherSchoolUser = toOtherSchoolUser(match, schoolId)
  }
  return analysis
}

export function roleLabel(role) {
  return ROLE_DISPLAY_NAMES[role] || String(role || '').replace(/_/g, ' ') || 'staff'
}

export function otherSchoolWhere(user) {
  const names = user?.schoolNames || []
  if (names.length === 1) return ` at ${names[0]}`
  if (names.length > 1) return ` at ${names.join(', ')}`
  if (user?.allSchools) return ' as a district-wide case manager'
  return ' at another school'
}

export function otherSchoolOptionLabel(user) {
  const name = user?.userName || user?.email || 'This person'
  const role = roleLabel(user?.role)
  if (user?.addAction === 'link_only') {
    return `${name} — ${role} (link only, no campus added)`
  }
  return `${name} — ${role}${otherSchoolWhere(user)} (add this campus)`
}

export function otherSchoolNote(user) {
  if (!user?.uid) return ''
  const name = user.userName || user.email || 'This person'
  const role = roleLabel(user.role)
  const where = otherSchoolWhere(user)
  if (user.addAction === 'refuse' && user.addReason === 'pending') {
    return `${name} is already in the app as ${role}${where}, but their account is pending. A district admin must activate them before they can be added here.`
  }
  if (user.addAction === 'refuse' && user.addReason === 'suspended') {
    return `${name} is already in the app as ${role}${where}, but their account is suspended. A district admin must restore them before they can be added here.`
  }
  if (user.addAction === 'refuse') {
    return `${name} is already in the app as ${role}${where}, but their account cannot be added here.`
  }
  if (user.addAction === 'link_only') {
    return `${name} is already in the app as ${role} and already reaches this school — link without adding a campus.`
  }
  return `${name} is already in the app as ${role}${where}. Add this campus to their account and link. Their role stays the same.`
}

export function canOfferOtherSchoolLink(user, canCreateStaff) {
  if (!user?.uid) return false
  if (user.addAction === 'refuse') return false
  if (user.addAction === 'link_only') return true
  return !!canCreateStaff
}

async function callLookup(schoolId, emails) {
  const unique = [...new Set((emails || []).map(normalizeStaffEmail).filter(Boolean))].slice(
    0,
    EMAIL_CAP
  )
  if (!schoolId || !unique.length) return []
  const fn = httpsCallable(functions, 'lookupUsersByEmail')
  const { data } = await fn({ schoolId, emails: unique })
  return Array.isArray(data?.matches) ? data.matches : []
}

export async function lookupUsersByEmail(schoolId, emails) {
  return callLookup(schoolId, emails)
}

export async function addCurrentSchoolToUser(uid, schoolId) {
  const id = String(uid || '').trim()
  const school = String(schoolId || '').trim()
  if (!id || !school) {
    throw new Error('User and school are required.')
  }
  const fn = httpsCallable(functions, 'addCurrentSchoolToUser')
  const { data } = await fn({ uid: id, schoolId: school })
  return data || { ok: true }
}
